import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import toastCommonProps from "../../../../common/toast";
import api from "../../../../common/api";
import HeadCont from "../../components/common/HeadCont";
import Btn from "../../components/common/Btn";
import InputBox from "../../components/common/InputBox";
import TitleBox from "../../components/common/TitleBox";

const FindPass = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [active, setActive] = useState("ready");
  const [errorMsg, setErrorMsg] = useState("");
  const [isEmailSent, setIsEmailSent] = useState(false);

  const onChangeInput = (e) => {
    setEmail(e.target.value);
    setErrorMsg("");

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(e.target.value)) {
      setActive("on");
    } else {
      setActive("ready");
    }
  };

  const onSendResetEmail = async () => {
    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMsg(t('password.invalid_email', 'Please enter a valid email address'));
      return;
    }

    try {
      const res = await api.post('/auth/forgot-password', { email });

      if (res.data.ok) {
        setIsEmailSent(true);
        toast(
          <p>{t('password.reset_email_sent', 'Password reset email has been sent. Please check your email.')}</p>,
          toastCommonProps("bottom-center", "toast_success", 3000)
        );
      } else {
        // Show the error message from backend (e.g., "This email is registered with Google authentication")
        setErrorMsg(res.data.error);
        setActive("ready");
      }
    } catch (err) {
      console.error('Error sending reset email:', err);
      setErrorMsg(t('password.reset_email_error', 'Failed to send reset email. Please try again.'));
      setActive("ready");
    }
  };

  return (
    <>
      <HeadCont
        title={t('password.find_title', 'Find Password')}
        underline={true}
        onLeftAction={() => navigate(-1)}
      />

      {!isEmailSent ? (
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox
            option={2}
            title={t('password.enter_email', 'Enter your email address')}
          />
          <p className="text-body2_400 text-gray_160">
            {t('password.email_help', 'We will send you a password reset link to your email.')}
          </p>
          <InputBox
            option="common"
            placeholder={t('password.email_placeholder', 'example@email.com')}
            onChangeInput={onChangeInput}
            value={email}
            setValue={setEmail}
            setErrorMsg={setErrorMsg}
            inputM={"email"}
            active={active}
            errorMsg={errorMsg}
          />
        </section>
      ) : (
        <section className="mx-6 mt-[80px] flex flex-col gap-6 items-center text-center">
          <img
            src="/images/mobile/icon/email_sent.svg"
            alt="Email sent"
            className="w-20 h-20"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div className="space-y-3">
            <h2 className="text-subtitle1 text-gray_180">
              {t('password.check_email', 'Check your email')}
            </h2>
            <p className="text-body2_400 text-gray_160">
              {t('password.reset_instructions', 'We sent a password reset link to:')}
            </p>
            <p className="text-body2_600 text-main">{email}</p>
            <p className="text-body2_400 text-gray_160 pt-4">
              {t('password.email_expires', 'This link will expire in 1 hour.')}
            </p>
          </div>
          <div className="flex flex-col gap-3 w-full pt-6">
            <button
              onClick={() => {
                setIsEmailSent(false);
                setEmail("");
                setActive("ready");
                setErrorMsg("");
              }}
              className="text-body2_500l text-gray_160 underline"
            >
              {t('password.try_different_email', 'Try a different email')}
            </button>
            <button
              onClick={onSendResetEmail}
              className="text-body2_500l text-main underline"
            >
              {t('password.resend_email', 'Resend email')}
            </button>
          </div>
        </section>
      )}

      {!isEmailSent && (
        <Btn
          option={"bottom"}
          bgColor={"bg-main"}
          textColor={"text-white"}
          borderColor={"border border-main"}
          active={active}
          onAction={onSendResetEmail}
          title={t('password.send_reset_link', 'Send Reset Link')}
        />
      )}
    </>
  );
};

export default FindPass;
